// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — "where it actually turned". Principle #9 asks for what everyone MISSED, not just what
// they scored, and in this game that is a specific moment: the turn where the loser's position fell
// away. So the log is replayed, the position scored after every one of the loser's turns, and the
// single biggest drop is reported together with the move the search would have played instead.
//
// It runs in slices off setTimeout — never rAF, which a backgrounded tab throttles to nothing — and
// it is cancellable, because the results screen must never be waiting on it to paint.

import { searchTurn } from './bot';
import { evaluate } from './bot';
import { cellName, createState, isOver, plyArrow, plyFrom, plyTo, step, type State } from './rules';

export interface Verdict {
  /** 1-based turn number for the losing side. */
  turn: number;
  /** What they played, and what the search would have played instead. */
  played: string;
  better: string;
  /** How much ground the position gave up on that turn, in evaluation points. */
  drop: number;
  name: string;
  isSelf: boolean;
}

export interface Analysis {
  cancel(): void;
}

const describe = (s: State, ply: number): string =>
  `${cellName(s, plyFrom(ply))}→${cellName(s, plyTo(ply))} ✕${cellName(s, plyArrow(ply))}`;

/**
 * Replay the round and find the losing side's worst turn. `onDone` fires once; if the round was too
 * short, or ended in a walkover, nothing is reported rather than something invented.
 */
export function analyse(
  modeId: string,
  seed: number,
  log: number[],
  loser: number,
  who: { name: string; isSelf: boolean },
  onDone: (v: Verdict | null) => void,
): Analysis {
  let cancelled = false;
  let i = 0;
  const s = createState(modeId, seed);
  let worstDrop = -Infinity;
  let worstTurn = 0;
  let worstPlayed = '';
  let worstBetter = '';
  let turnNo = 0;

  const slice = (): void => {
    if (cancelled) return;
    const until = Date.now() + 24;
    while (i < log.length && Date.now() < until) {
      const ply = log[i];
      if (s.turn === loser && !isOver(s)) {
        turnNo++;
        const before = evaluate(s, loser);
        const best = searchTurn(s, { depth: 1, timeMs: 0, nodes: 4000, kq: 8, ka: 3 });
        const probeText = describe(s, ply);
        const betterText = best.ply >= 0 ? describe(s, best.ply) : '';
        // Score the position from the loser's point of view AFTER their move, so a drop is ground
        // genuinely given away rather than the ordinary decay of a board burning down.
        const after = createState(modeId, seed);
        for (let j = 0; j <= i; j++) if (!step(after, log[j], undefined, true, false)) break;
        const drop = before - evaluate(after, loser);
        if (drop > worstDrop && best.ply >= 0 && best.ply !== ply) {
          worstDrop = drop;
          worstTurn = turnNo;
          worstPlayed = probeText;
          worstBetter = betterText;
        }
      }
      if (!step(s, ply, undefined, true, false)) break;
      i++;
    }
    if (cancelled) return;
    if (i < log.length) {
      setTimeout(slice, 0);
      return;
    }
    onDone(
      worstTurn > 0 && worstDrop > 0
        ? { turn: worstTurn, played: worstPlayed, better: worstBetter, drop: Math.round(worstDrop), name: who.name, isSelf: who.isSelf }
        : null,
    );
  };

  setTimeout(slice, 0);
  return {
    cancel() {
      cancelled = true;
    },
  };
}
